<script setup lang="ts">
    import BackButton from '@/components/BackButton.ce.vue';
    import Card from '@/components/quote/cards/Card.ce.vue';
    import { type EventTypeTyping, type ProductCategoryTyping, type ProductTyping } from '@/typings/widget';
    import Icon from '@shared/components/ui/icon/icon.vue';
    import { ProductPricingStrategy } from '@shared/constants/payments';
    import { type IdTyping } from '@shared/typings';

    const props = defineProps<{
        modelValue: IdTyping[]
        products: ProductTyping[]
        categories: ProductCategoryTyping[]
        isInPath?: boolean
    }>();

    // eslint-disable-next-line func-call-spacing
    const emit = defineEmits<{
        (event: 'update:modelValue', value: IdTyping[]): void
        (event: 'next' | 'goBack'): void
    }>();

    // Stores
    const { productCategoryId, decorationId, entertainmentId, guestTypeIds, eventTypeId } = storeToRefs(useWidgetPathStore());
    const { venue, venueEventTypes } = storeToRefs(useWidgetVenueStore());

    const { t } = useI18n();

    // Refs
    const categoriesRef = ref<HTMLElement | null>(null);
    const productIdsModel: Ref<IdTyping[]> = ref(props.modelValue);
    const showScrollLeft = ref(false);
    const showScrollRight = ref(false);

    // Computed
    const selectedEventType: ComputedRef<EventTypeTyping | undefined > = computed(() => venueEventTypes.value.find(eventType => eventType.id === eventTypeId.value));
    const filteredProducts = computed(() => {
        // First, filter products by allowed event type
        const eventFilteredProducts = props.products.filter(product => product.eventTypes.some(eventType => eventType.id === eventTypeId.value));

        // Then, filter products by guestTypeIds in the quote
        const guestFilteredProducts = eventFilteredProducts.filter(product => {
            if (product.pricingStrategy === ProductPricingStrategy.PerGuesttype) {
                if (props.isInPath) {
                    const guestPricesIds = product.guestTypePrices.map(price => price.guestType.id);
                    return product.guestTypes.some(guestType => guestPricesIds.includes(guestType.id));
                }
                return product.guestTypes.some(guestType => guestTypeIds.value.includes(guestType.id));
            }
            return true;
        });

        // Finally, filter by selected category
        return guestFilteredProducts.filter(product => product.categories.some(category => category.id === productCategoryId.value));
    });

    // Methods
    const handleSelectedProducts = (id: IdTyping): void => {
        const indexFound: IdTyping = productIdsModel.value?.findIndex(productId => productId === id);
        if (indexFound > -1) productIdsModel.value?.splice(indexFound, 1);
        else productIdsModel.value.push(id);
    };

    const updateScrollButtonsVisibility = (): void => {
        if (!categoriesRef.value) return;

        const item = categoriesRef.value;
        showScrollLeft.value = item.scrollLeft > 0;
        showScrollRight.value = item.scrollLeft + item.clientWidth < item.scrollWidth;
    };

    const scrollCategoriesLeft = (): void => {
        if (categoriesRef.value) {
            categoriesRef.value.scrollTo({
                left: 0,
                behavior: 'smooth',
            });

            // Wait for scroll animation to complete
            setTimeout(updateScrollButtonsVisibility, 300);
        }
    };

    const scrollCategoriesRight = (): void => {
        if (categoriesRef.value) {
            categoriesRef.value.scrollTo({
                left: categoriesRef.value.scrollWidth,
                behavior: 'smooth',
            });
            setTimeout(updateScrollButtonsVisibility, 300);
        }
    };

    // Hooks
    onMounted(async() => {
        if (!productIdsModel.value?.includes(decorationId.value)) productIdsModel.value?.push(decorationId.value);
        if (!productIdsModel.value?.includes(entertainmentId.value)) productIdsModel.value?.push(entertainmentId.value);

        await nextTick(() => {
            updateScrollButtonsVisibility();
            categoriesRef.value?.addEventListener(
                'scroll', updateScrollButtonsVisibility, { passive: true },
            );
        });
    });

    onBeforeUnmount(() => {
        categoriesRef.value?.removeEventListener('scroll', updateScrollButtonsVisibility);
    });

    // Watchers
    watch(() => productIdsModel.value, nVal => {
        if (nVal) emit('update:modelValue', nVal);
    });
</script>

<template>
    <div class="shop">
        <div
            v-if="isInPath"
            class="shop__header"
        >
            <BackButton
                :on-click="() => emit('goBack')"
            />
        </div>
        <div
            v-if="isInPath"
            class="shop__title"
        >
            <h2>{{ t('widget.shop.title', { event: selectedEventType?.name }) }}</h2>
        </div>
        <div class="shop__categories">
            <button
                v-if="showScrollLeft"
                type="button"
                class="btn btn--icon btn--secondary shop__scroll shop__scroll--left"
                @click="scrollCategoriesLeft"
            >
                <Icon
                    name="chevronLeft"
                    size="medium"
                />
            </button>
            <div
                ref="categoriesRef"
                class="shop__categories__content"
            >
                <button
                    v-for="category in categories"
                    :key="category.id"
                    :class="{'shop__category--selected': category.id === productCategoryId}"
                    type="button"
                    class="shop__category btn"
                    @click="productCategoryId = category.id"
                >
                    <span>
                        {{ category.name }}
                    </span>
                </button>
            </div>
            <button
                v-if="showScrollRight"
                type="button"
                class="btn btn--icon btn--secondary shop__scroll shop__scroll--right"
                @click="scrollCategoriesRight"
            >
                <Icon
                    name="chevronRight"
                    size="medium"
                />
            </button>
        </div>
        <div class="shop__products">
            <template v-if="filteredProducts.length">
                <Card
                    v-for="product in filteredProducts"
                    :key="product.id"
                    :item="product"
                    :selected="(productIdsModel as IdTyping[]).includes(product.id)"
                    :show-price="venue?.showProductPrices"
                    @click="handleSelectedProducts(product.id)"
                />
            </template>
            <div
                v-else
                class="shop__no-products"
            >
                <span>
                    {{ t('widget.global.no-products') }}
                </span>
            </div>
        </div>
        <button
            type="button"
            class="btn btn--primary shop__next"
            @click="emit('next')"
        >
            <span>
                {{ t('widget.quote.category.skip') }}
            </span>
        </button>
    </div>
</template>

<style lang="scss" scoped>
.shop {
    position: relative;
    display: flex;
    flex-direction: column;

    &__header {
        background: var(--background-primary);
        inline-size: 100%;
        line-height: 1;
        margin-inline: 0;
        padding-block: var(--spacing-04) 0;
        padding-inline: var(--widget-inner-gutter);
    }

    &__title {
        display: flex;
        justify-content: center;
        background: var(--background-primary);

        h2 {
            padding-inline: var(--spacing-03);
            text-align: center;
            text-transform: lowercase;

            @include min-width(md) {
                text-align: start;
            }

            &::first-letter {
                text-transform: uppercase;
            }
        }
    }

    &__categories {
        position: sticky;
        z-index: var(--zindex-stack-twice);
        background: var(--background-primary);
        inline-size: 100%;
        inset-block-start: 0;

        @include min-width(lg) {
            border-block-end: 1px solid var(--border-quaternary);
        }

        &__content {
            display: flex;
            flex-wrap: nowrap;
            align-items: center;
            justify-content: flex-start;
            gap: var(--spacing-02);
            inline-size: 100%;
            -ms-overflow-style: none;
            overflow-x: auto;
            padding-block: var(--spacing-05);
            padding-inline: var(--spacing-03);
            scrollbar-width: none;

            @include min-width(md) {
                padding-block: var(--spacing-04);
                padding-inline: var(--spacing-04);
            }

            @include min-width(lg) {
                padding-block: var(--spacing-05);
                padding-inline: var(--spacing-07);
            }

            &::-webkit-scrollbar {
                display: none;
            }
        }
    }

    &__category {
        border: 1px solid var(--color-white);
        border-radius: var(--border-radius-sm);
        background: rgb(255 255 255 / 0.4);
        block-size: var(--size-10);
        padding-inline: var(--spacing-03);

        @include min-width(md) {
            block-size: var(--size-14);
            min-inline-size: 196px;
        }

        @include min-width(lg) {
            block-size: var(--size-15);
        }

        span {
            overflow: hidden;
            font-weight: var(--font-weight-light);
            text-overflow: ellipsis;
        }

        &--selected {
            border-color: var(--color-black-space-10);
            background-color: var(--color-pure-white);
            font-weight: var(--font-weight-medium);
        }
    }

    &__products {
        display: grid;
        overflow: auto;
        block-size: 100%;
        gap: var(--spacing-03);
        grid-template-columns: repeat(2, 1fr);
        inline-size: 100%;
        padding-block: 0 var(--spacing-16);
        padding-inline: var(--spacing-03);

        @include min-width(md) {
            gap: var(--spacing-02);
            grid-template-columns: repeat(4, 1fr);
            padding-inline: var(--spacing-04);
        }

        @include min-width(lg) {
            padding-block: var(--spacing-05) var(--spacing-16);
            padding-inline: var(--spacing-07);
        }
    }

    &__next {
        position: fixed;
        z-index: var(--zindex-stack-twice);
        align-self: center;
        inset-block-end: var(--spacing-05);
        inset-inline-start: 50%;
        min-inline-size: 174px;
        transform: translateX(-50%);

        @include min-width(lg) {
            margin-block-end: var(--spacing-06);
            transform: none;
        }

        @include min-width(lg) {
            inset-block-end: var(--spacing-04);
            inset-inline: auto var(--spacing-04);
            margin-block-end: 0;
        }
    }

    &__scroll {
        position: absolute;
        z-index: var(--zindex-stack-twice);
        inset-block-start: 50%;
        transform: translateY(-50%);

        &--left {
            inset-inline-start: var(--spacing-03);
        }

        &--right {
            inset-inline-end: var(--spacing-03);
        }
    }
}
</style>
